/**
 * Плашка «Не сдали» (задача 71 §5.3) — свёрнута/раскрыта.
 *
 * Макап M6/D6 `DESKTOP STATE 2`: список несдавших (§2.2 `useMissing`) + кнопки
 * «Внести» / «Отсутствие» на каждом субъекте. «Внести» → док-панель (`EntryDock`),
 * «Отсутствие» → `AbsenceFormDialog` (отсутствие на диапазон дат, задача 82
 * §1.2). После действия субъект уходит из плашки (инвалидация missing-кэша в
 * хуках). caution-тон — это очередь действий.
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp } from "lucide-react";

import { Surface } from "@/components/ui/surface";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PersonNameLink } from "@/components/people/PersonNameLink";
import type { MissingSubject } from "@/api/approval";

interface MissingPanelProps {
  subjects: MissingSubject[];
  onEnter: (subject: MissingSubject) => void;
  onAbsence: (subject: MissingSubject) => void;
}

export function MissingPanel({
  subjects,
  onEnter,
  onAbsence,
}: MissingPanelProps): React.ReactElement | null {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState(true);

  if (subjects.length === 0) return null;

  return (
    <Surface
      className="flex flex-col gap-0 overflow-hidden p-0"
      data-testid="approval-missing-panel"
    >
      <button
        type="button"
        data-testid="approval-missing-toggle"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-2 px-4 py-3 text-left"
        style={{ borderBottom: expanded ? "1px dashed var(--rule-strong)" : "none" }}
      >
        <span
          className="font-mono text-sm font-semibold"
          style={{ color: "var(--tone-danger-fg)" }}
          aria-hidden="true"
        >
          ∅
        </span>
        <span className="text-sm font-semibold text-[var(--fg-1)]">
          {t("timeApproval.missingTitle", { count: subjects.length })}
        </span>
        <span className="hidden font-mono text-[11px] text-[var(--fg-3)] sm:inline">
          {t("timeApproval.missingHint")}
        </span>
        <span className="flex-1" />
        <span className="inline-flex items-center gap-1 text-xs text-[var(--fg-2)]">
          {expanded ? t("timeApproval.missingCollapse") : t("timeApproval.missingExpand")}
          {expanded ? (
            <ChevronUp className="h-4 w-4" aria-hidden />
          ) : (
            <ChevronDown className="h-4 w-4" aria-hidden />
          )}
        </span>
      </button>

      {expanded && (
        <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 xl:grid-cols-3">
          {subjects.map((s) => (
            <div
              key={s.id}
              data-testid={`approval-missing-${s.id}`}
              className="flex flex-col gap-2 rounded-[var(--radius-card)] border border-[var(--rule-line)] p-3"
            >
              <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <PersonNameLink
                    id={s.id}
                    name={s.display_name}
                    fullName={s.full_name}
                    className="block truncate text-sm font-semibold text-[var(--fg-1)] hover:underline"
                  />
                  {s.position && (
                    <div className="truncate font-mono text-[11px] text-[var(--fg-3)]">
                      {s.position}
                    </div>
                  )}
                </div>
                <Badge tone="danger">
                  <span className="font-mono" aria-hidden="true">
                    ∅
                  </span>
                  {t("timeApproval.missingBadge")}
                </Badge>
              </div>
              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  data-testid={`approval-missing-${s.id}-enter`}
                  onClick={() => onEnter(s)}
                >
                  {t("timeApproval.enter")}
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  data-testid={`approval-missing-${s.id}-absence`}
                  onClick={() => onAbsence(s)}
                >
                  {t("timeApproval.absence")}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Surface>
  );
}
